import React, { useEffect } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'

import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/courses.action'
import ImageFull from 'components/ui/image-full/ImageFull'
import { CourseTypeDic } from 'enum/course-type.enum'
import Loader from 'components/ui/loader/Loader'
import { dateParse } from 'helpers/date.helper'
import { sanitizeHtml } from 'helpers/sanitizeHtml'
import {
  Tags,
  synchronousTypes,
} from 'components/ui/CourseCards/components/atoms/Tags'
import { SynchronousCourseTypes } from 'enum'
import * as General from 'store/actions/general.action'
import {
  IntegrationsProvider,
  useIntegrations,
} from 'contexts/IntegrationsContext'
import { courseParser } from 'helpers/courseParser'
import { ExternalCourseIndicator } from 'components/ui/CourseCards/components/molecules/externalCourseIndicator'
import { GoBackButton } from 'components/home/components/Buttons/GoBackButton'
import { ProgressBar } from '../../../ui/LearningPathCard/components/atoms/ProgressBar/index'
import EventTarget from './components/event-target/EventTarget'
import { Prerequisites } from './components/prerequisites'
import styles from './Get.module.scss'
import {
  PrerequisitesProvider,
  usePrerequisites,
} from './contexts/Prerequisites'
import Wrapper from 'components/reusables/wrapper'

interface GetProps {
  match: {
    params: {
      courseId: string
    }
  }
}

const Get: React.FC<GetProps> = ({
  match: {
    params: { courseId },
  },
}: any) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { sence } = useIntegrations()
  const { course, loading } = useSelector(({ courses }: IMainState) => courses)
  const { synchronousCourseType, synchronousCourseSource } = {
    ...course?.synchronousCourse,
  }

  const synchronousType =
    synchronousCourseType && synchronousTypes[synchronousCourseType]

  const { lessons } = course
  useEffect(() => {
    dispatch({ type: fromActions.GET_COURSE, payload: courseId })
    dispatch(General.Actions.getIntegrationsList())
  }, [courseId, dispatch])

  const translateCourseType = CourseTypeDic[Number(course.courseType)]

  const isSynchronous = !!course.synchronousCourse

  const prefix = 'courses.show.content'

  const isIntegrationCourse = course['integrationCourseLink']

  const prerequisites = usePrerequisites()

  if (loading || sence.loading) return <Loader overlay color />

  const assigned = course.assigned

  const disabledActions = prerequisites.isPending
  return (
    <>
      <GoBackButton />
      <Container
        className={classNames(
          styles.container,
          !sence.authenticated && styles.blur
        )}
      >
        <Wrapper shadow={true}>
          <Row>
            {course && (
              <Col>
                <Row className="mb-4">
                  <Col xs={12} className={`${styles.banner} mb-4`}>
                    <div className="position-relative">
                      <Tags
                        course={courseParser(course)}
                        enrol={course.cardEnrolData}
                      />
                    </div>
                    <ImageFull src={course.bannerUrl} />
                    <ExternalCourseIndicator
                      origin="COURSE_DETAIL"
                      url={course['integrationCourseLink']}
                    />
                  </Col>
                  <Col xs={6} className="pr-1">
                    <h1 className={styles.title}>{course.name}</h1>
                  </Col>
                  <Col
                    xs={6}
                    className={`mb-1 ${disabledActions ? styles.disabled : ''}`}
                  >
                    <EventTarget
                      course={course}
                      disabled={disabledActions}
                      assigned={assigned}
                    />
                  </Col>
                  {assigned && (
                    <Col xs={12} className="my-4">
                      <ProgressBar
                        progressPercentage={
                          course.cardEnrolData?.progressPercentage || 0
                        }
                        advance={course.cardEnrolData?.advance}
                        styleSize="medium"
                      />
                    </Col>
                  )}
                  {disabledActions && !prerequisites.isLoading && (
                    <Col xs={12}>
                      <div className={styles.disabledBanner}>
                        <div className="d-flex">
                          <div className="mr-2">
                            <FontAwesomeIcon icon={faInfoCircle} />
                          </div>
                          <div className={styles.text}>
                            {t(
                              'components.courseCard.alerts.withPendingPrerequisites'
                            )}
                          </div>
                        </div>
                      </div>
                    </Col>
                  )}
                  <Col xs={12} className={`my-4 ${styles.description}`}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(course.description),
                      }}
                    />
                  </Col>
                  <Col xs={6} sm={4}>
                    <p className={styles.allHeads}>
                      {t(`${prefix}.courseType`)}
                    </p>
                    <p className={styles.description}>
                      <span>
                        {t(
                          `${prefix}.${
                            course.mandatory ? 'mandatory' : 'optional'
                          }`
                        )}
                      </span>
                      {synchronousType && (
                        <p>
                          {t(`components.courseCard.${synchronousType.title}`)}
                        </p>
                      )}
                    </p>
                  </Col>
                  {!isSynchronous && (
                    <Col xs={6} sm={4}>
                      <p className={styles.allHeads}>
                        {t(`${prefix}.lessons`)}
                      </p>

                      <p className={styles.description}>
                        {!isIntegrationCourse ? `${lessons.length || 0} ` : '-'}
                      </p>
                    </Col>
                  )}
                  {!isSynchronous && (
                    <Col xs={6} sm={4}>
                      <p className={styles.allHeads}>
                        {t(`${prefix}.courseAccess`)}
                      </p>
                      <p className={styles.description}>
                        {t(`commons.course.type.${translateCourseType}`)}
                      </p>
                    </Col>
                  )}
                  <Col xs={6} sm={4}>
                    <p className={styles.allHeads}>
                      {t(`${prefix}.startDate`)}
                    </p>
                    <p className={styles.description}>
                      {t(dateParse(course.startDate))}
                    </p>
                  </Col>
                  <Col xs={6} sm={4}>
                    <p className={styles.allHeads}>
                      {t(`${prefix}.untilDate`)}
                    </p>
                    <p className={styles.description}>
                      {t(dateParse(course.untilDate))}
                    </p>
                  </Col>
                  <Col xs={6} sm={4}>
                    <p className={styles.allHeads}>
                      {t(`${prefix}.totalHours`)}
                    </p>
                    <p>
                      {course.hours === 0 || course.hours === null
                        ? '-'
                        : `${course.hours} ${t(`${prefix}.hours`)}`}
                    </p>
                  </Col>
                  {synchronousCourseType ===
                    SynchronousCourseTypes.faceToFace &&
                    synchronousCourseSource && (
                      <Col xs={12} sm={6}>
                        <p className={styles.allHeads}>
                          {t(`${prefix}.location`)}
                        </p>
                        <p className={styles.description}>
                          {synchronousCourseSource.value}
                        </p>
                      </Col>
                    )}
                </Row>
              </Col>
            )}
          </Row>
        </Wrapper>
        <Prerequisites />
      </Container>
    </>
  )
}

export default (props: GetProps) => (
  <IntegrationsProvider>
    <PrerequisitesProvider courseId={+props.match.params.courseId}>
      <Get {...props} />
    </PrerequisitesProvider>
  </IntegrationsProvider>
)
